"use client";

import { Camera } from "@/components/Camera";
import { RenderedDice } from "@/components/RenderedDice";
import {
  DiceActionTypes,
  getMediaUrl,
  isDebug,
  PddPayloadTypes,
  SHADOW_OPACITY,
} from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { Context } from "@/context";
import { getManifestData } from "@/helpers/getManifestData";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { Environment, Plane } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { lazy, Suspense, useContext, useEffect, useRef } from "react";
import {
  BasicShadowMap,
  NoToneMapping,
  PCFShadowMap,
  PCFSoftShadowMap,
  type ShadowMapType,
} from "three";

const EffectComposer = lazy(() =>
  import("@react-three/postprocessing").then((module) => ({
    default: module.EffectComposer,
  })),
);
const Bloom = lazy(() =>
  import("@react-three/postprocessing").then((module) => ({
    default: module.Bloom,
  })),
);
const shadowPresets: Record<
  number,
  {
    scale: number;
    type: ShadowMapType;
  }
> = {
  1: {
    scale: 0.25,
    type: BasicShadowMap,
  },
  2: {
    scale: 0.5,
    type: PCFShadowMap,
  },
  3: {
    scale: 1,
    type: PCFSoftShadowMap,
  },
};
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const nearestPowerOfTwo = (value: number) =>
  2 ** Math.round(Math.log2(Math.max(1, value)));

export const RenderedScene = ({ port }: { port?: MessagePort } = {}) => {
  // When running on the main thread (isMobileApp), communication goes through
  // the provided MessagePort instead of the WorkerGlobalScope self.
  const messenger: Pick<
    typeof self,
    "addEventListener" | "removeEventListener" | "postMessage"
  > = port ?? self;
  const {
    addRenderedRoll,
    addManifest,
    resetRenderedDiceStore,
    setManifests,
    setDiceSets,
    setUserSettings,
  } = useRenderedDiceStore();
  const { viewport, size, gl } = useThree();
  const { customSetOverrideId, setCustomSetOverrideId, setCustomUrlMap } =
    useDicePreviewerStore();
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const { nodeEnv: ne, env: e, user: u } = useContext(Context);
  const nameJson = self.name ? JSON.parse(self.name) : {};
  const nodeEnv = ne || nameJson.nodeEnv;
  const env = e || nameJson.env;
  const user = u || nameJson.user;
  const mediaUrl = getMediaUrl(nodeEnv!, env!);
  const userSettings = useRenderedDiceStore((state) => state.userSettings);
  const shadowMapSize = (() => {
    if (!userSettings?.shadowQuality) return undefined;

    const presetScale = shadowPresets[userSettings.shadowQuality]?.scale ?? 1;
    const min = 256;
    // keep this conservative: huge shadow maps can exceed GPU limits/tank perf
    const max = clamp(gl.capabilities.maxTextureSize, min, 4096);
    const targetW = size.width * presetScale;
    const targetH = size.height * presetScale;
    const width = clamp(nearestPowerOfTwo(Math.round(targetW)), min, max);
    const height = clamp(nearestPowerOfTwo(Math.round(targetH)), min, max);

    return [width, height];
  })();

  // OffScreenCanvas does not give us DOM access, so we can't use Drei's
  // <Stats/> here. Instead, gather stats and send to main thread every second
  // while in debug mode.
  const frames = useRef(0);
  useFrame(() => {
    if (!isDebug) return;

    gl.info.reset();
    frames.current++;
  });
  useEffect(() => {
    if (!isDebug) return;

    // eslint-disable-next-line react-hooks/immutability
    gl.info.autoReset = false;

    let lastTime = performance.now();
    let fps = 0;

    const interval = setInterval(() => {
      const currentTime = performance.now();
      const delta = currentTime - lastTime;
      fps = Math.round((frames.current * 1000) / delta);
      frames.current = 0;
      lastTime = currentTime;

      const info = gl.info;
      messenger.postMessage({
        type: PddPayloadTypes.WorkerStats,
        payload: {
          fps,
          ms: delta / 1000,
          calls: info.render.calls,
          triangles: info.render.triangles,
          points: info.render.points,
          lines: info.render.lines,
          geometries: info.memory.geometries,
          textures: info.memory.textures,
          programs: info.programs?.length,
        },
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [gl, messenger]);

  useEffect(() => {
    const handleMessage = async ({
      data: { type, payload: message },
    }: MessageEvent) => {
      const getSetManifest = async (setId: string) => {
        const currentState = useRenderedDiceStore.getState();
        const {
          customSetOverrideId: currentCustomSetOverrideId,
          customUrlMap,
        } = useDicePreviewerStore.getState();
        const manifestUrl = customUrlMap
          ? customUrlMap[customSetManifestFileName]
          : currentState.diceSets?.find(({ id }) => id === setId)?.manifestUrl;
        const overrideSetId =
          customUrlMap && currentCustomSetOverrideId
            ? currentCustomSetOverrideId
            : undefined;

        if (!manifestUrl) {
          console.error(
            `[RenderedScene] Manifest URL not found for set ID "${setId}". Cannot load manifest or start roll.`,
          );

          return;
        }

        if (currentState.manifests.ids.includes(setId)) {
          if (isDebug) {
            console.log(
              `[RenderedScene] Manifest for set ID "${setId}" already exists in store. Returning existing manifest.`,
            );
          }

          return {
            manifest: currentState.manifests.entities[setId],
            preloaded: true,
          };
        }

        if (isDebug) {
          console.log(
            `[RenderedScene] Fetching manifest for set ID "${setId}" from URL: ${manifestUrl}`,
          );
        }

        const manifest = await getManifestData({
          mediaUrl,
          setId,
          overrideSetId,
          manifestUrl,
          customUrlMap,
        });

        if (!manifest) {
          console.error(
            `[RenderedScene] Manifest for set ID "${setId}" could not be loaded.`,
          );

          return;
        }

        addManifest(setId, manifest);

        return { manifest };
      };

      if (type === PddPayloadTypes.RenderDice) {
        if (isDebug) {
          console.log("[RenderedScene] Received renderDice message:", message);
        }

        addRenderedRoll(message);
      } else if (type === PddPayloadTypes.Manifests) {
        if (isDebug) {
          console.log("[RenderedScene] Received manifests message:", message);
        }

        setManifests(message);
      } else if (type === PddPayloadTypes.UserSettings) {
        setUserSettings(message);
      } else if (type === PddPayloadTypes.DiceSets) {
        setDiceSets(message);
      } else if (type === PddPayloadTypes.CustomUrlMap) {
        setCustomUrlMap(message);
      } else if (type === PddPayloadTypes.CustomSetOverrideId) {
        setCustomSetOverrideId(message);
      } else if (type === DiceActionTypes.SetUpdate) {
        const result = await getSetManifest(message);
        const manifest = result?.manifest;
        const preloaded = result?.preloaded;

        messenger.postMessage({
          type: DiceActionTypes.SetUpdate,
          payload: {
            setId: message,
            manifest,
            preloaded,
          },
        });
      } else if (type === PddPayloadTypes.ResetStore) {
        if (isDebug) {
          console.log("[RenderedScene] Resetting dice store");
        }

        resetRenderedDiceStore();
      } else if (type === PddPayloadTypes.RemoveEventListener) {
        messenger.removeEventListener("message", handleMessage);
      } else if (type === PddPayloadTypes.PreRoll) {
        if (isDebug) {
          console.log("[RenderedScene] Received PreRoll message:", message);
        }

        const result = await getSetManifest(message.data.setId);
        const manifest = result?.manifest;
        const preloaded = result?.preloaded;

        if (isDebug) {
          console.log(
            "[RenderedScene] Sending PreRoll message to renderedWorkerListeners with manifest:",
            manifest,
          );
        }

        messenger.postMessage({
          type: PddPayloadTypes.PreRoll,
          payload: {
            message,
            manifest,
            preloaded,
          },
        });
      }
    };

    messenger.addEventListener("message", handleMessage);
    messenger.postMessage({ type: PddPayloadTypes.ComponentMounted });

    if (isDebug) {
      console.log("RenderedScene mounted");
    }

    return () => {
      messenger.removeEventListener("message", handleMessage);
    };
  }, [
    mediaUrl,
    setDiceSets,
    addManifest,
    setManifests,
    nodeEnv,
    env,
    user.id,
    customUrlMap,
    resetRenderedDiceStore,
    addRenderedRoll,
    customSetOverrideId,
    customUrlMap,
    setCustomSetOverrideId,
    setCustomUrlMap,
    setUserSettings,
    messenger,
  ]);

  useThree(({ gl }) => {
    gl.toneMapping = NoToneMapping;
    gl.shadowMap.enabled = true;

    if (userSettings) {
      gl.shadowMap.type = shadowPresets[userSettings.shadowQuality]?.type;
    }
  });

  return (
    <>
      {mediaUrl && (
        <Environment
          files={`${mediaUrl}/dice/images/common/environment.jpg`}
          environmentIntensity={1.0}
        />
      )}
      <ambientLight intensity={1.0} />
      {userSettings && shadowMapSize && (
        <directionalLight
          key={`shadow-${userSettings}-${shadowMapSize[0]}x${shadowMapSize[1]}`}
          position={[10, 10, 20]}
          intensity={1.0}
          castShadow
          shadow-mapSize={[shadowMapSize[0], shadowMapSize[1]]}
          shadow-camera-left={-viewport.width}
          shadow-camera-right={viewport.width}
          shadow-camera-top={viewport.height}
          shadow-camera-bottom={-viewport.height}
        />
      )}
      <RenderedDice />
      <Plane args={[viewport.width, viewport.height]} receiveShadow>
        <shadowMaterial
          transparent
          opacity={SHADOW_OPACITY}
          depthWrite={false}
        />
      </Plane>
      <Camera />
      <Suspense>
        {userSettings?.isPostProcessingEnabled && (
          <EffectComposer multisampling={0} renderPriority={99}>
            <Bloom
              intensity={1.0}
              luminanceThreshold={0.99}
              luminanceSmoothing={0.025}
              radius={0.3}
              mipmapBlur
            />
          </EffectComposer>
        )}
      </Suspense>
    </>
  );
};
